/**
 * TagPicker — popover for applying/removing tags on one or more records.
 *
 * Rendered through a portal (position: fixed) so it is never clipped by
 * scrollable table containers. Tags are grouped by category; typing filters
 * them, and when the text matches nothing it offers inline "Create" buttons
 * for every category that allows user-created tags (admin sees all).
 *
 * Props:
 * - entityType    required registry key (e.g. 'tracker_piping')
 * - projectId     project context (needed for project-scoped categories)
 * - selectedTags  tags currently applied (array; for multi-row use the common subset)
 * - onToggle(tag, nowSelected)  called after user clicks a tag
 * - onClose()
 * - anchorRect    DOMRect of the trigger (getBoundingClientRect())
 * - title         optional header, e.g. 'Tag 12 selected rows'
 * - onViewUsage(tag)  optional — shows a 🔎 button per tag that opens the
 *                     global usage explorer (parent renders TagUsageModal)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import TagChip from './TagChip';
import { fetchAvailableTags, fetchTagCategories, createTag, contrastText } from './tagApi';

const POPOVER_WIDTH = 300;

const TagPicker = ({ entityType, projectId, selectedTags = [], onToggle, onClose, anchorRect, title, onViewUsage }) => {
  const { instance, accounts } = useMsal();
  const [allTags, setAllTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  const selectedIds = useMemo(() => new Set(selectedTags.map((t) => t.id)), [selectedTags]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tags, cats] = await Promise.all([
        fetchAvailableTags(instance, accounts, { entityType, projectId }),
        fetchTagCategories(instance, accounts),
      ]);
      setAllTags(tags);
      setCategories(cats);
    } catch (e) {
      console.error('Failed to load tags:', e);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, entityType, projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { inputRef.current?.focus(); }, [loading]);

  // Close on outside click / Escape
  useEffect(() => {
    const onDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const term = search.trim().toLowerCase();
  const filtered = term ? allTags.filter((t) => t.name.toLowerCase().includes(term)) : allTags;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const tag of filtered) {
      if (!map.has(tag.category_id)) {
        map.set(tag.category_id, { name: tag.category_name, color: tag.category_color, tags: [] });
      }
      map.get(tag.category_id).tags.push(tag);
    }
    return [...map.entries()];
  }, [filtered]);

  const exactMatch = allTags.some((t) => t.name.toLowerCase() === term);
  // Categories the user may create the typed tag in. Filter to ones applying to
  // this entity type; project-scoped ones need a projectId to create in.
  const creatableCategories = useMemo(() => {
    if (!term || exactMatch) return [];
    return categories.filter((c) => {
      if (!c.allow_user_create) return false; // server enforces admin bypass; keep UI simple
      if (Array.isArray(c.applies_to) && c.applies_to.length && !c.applies_to.includes(entityType)) return false;
      if (c.scope === 'project' && (projectId == null || projectId === '')) return false;
      return true;
    });
  }, [categories, term, exactMatch, entityType, projectId]);

  const handleCreate = async (category) => {
    if (creating) return;
    setCreating(true);
    setError('');
    try {
      const tag = await createTag(instance, accounts, {
        categoryId: category.id,
        name: search.trim(),
        projectId: category.scope === 'project' ? projectId : undefined,
      });
      setAllTags((prev) => [...prev, tag]);
      setSearch('');
      onToggle(tag, true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create tag');
    } finally {
      setCreating(false);
    }
  };

  const handleTagClick = (tag) => {
    onToggle(tag, !selectedIds.has(tag.id));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) handleTagClick(filtered[0]);
      else if (creatableCategories.length === 1) handleCreate(creatableCategories[0]);
    }
  };

  // Position: below the anchor, flipping up if there is no room.
  const top = anchorRect
    ? (anchorRect.bottom + 340 > window.innerHeight ? Math.max(8, anchorRect.top - 344) : anchorRect.bottom + 4)
    : 100;
  const left = anchorRect
    ? Math.min(anchorRect.left, window.innerWidth - POPOVER_WIDTH - 12)
    : 100;

  const popover = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top,
        left,
        width: POPOVER_WIDTH,
        maxHeight: 340,
        backgroundColor: 'white',
        borderRadius: 10,
        boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
        border: '1px solid #e5e7eb',
        zIndex: 10050,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
        {title && (
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>{title}</div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search or create a tag..."
          style={{
            width: '100%', boxSizing: 'border-box', padding: '6px 10px',
            border: '1px solid #dee2e6', borderRadius: 7, fontSize: '0.8rem', outline: 'none',
          }}
        />
        {error && <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 4 }}>{error}</div>}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px 8px' }}>
        {loading ? (
          <div style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>
        ) : (
          <>
            {grouped.map(([catId, cat]) => (
              <div key={catId} style={{ marginBottom: 6 }}>
                <div style={{
                  fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase',
                  color: '#94a3b8', padding: '4px 6px 2px', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: cat.color, display: 'inline-block' }} />
                  {cat.name}
                </div>
                {cat.tags.map((tag) => {
                  const isSelected = selectedIds.has(tag.id);
                  return (
                    <div
                      key={tag.id}
                      onClick={() => handleTagClick(tag)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                        backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'transparent'; }}
                    >
                      <TagChip tag={tag} />
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {onViewUsage && (
                          <span
                            onClick={(e) => { e.stopPropagation(); onViewUsage(tag); }}
                            title="See everything carrying this tag"
                            style={{ fontSize: '0.75rem', opacity: 0.55, cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.55; }}
                          >
                            🔎
                          </span>
                        )}
                        {isSelected && <span style={{ color: '#2563eb', fontWeight: 700, fontSize: '0.8rem' }}>✓</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            {grouped.length === 0 && creatableCategories.length === 0 && (
              <div style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
                {term ? 'No matching tags.' : 'No tags yet.'}
              </div>
            )}

            {creatableCategories.length > 0 && (
              <div style={{ borderTop: grouped.length ? '1px solid #f1f5f9' : 'none', paddingTop: 6, marginTop: 4 }}>
                {creatableCategories.map((cat) => (
                  <div
                    key={cat.id}
                    onClick={() => handleCreate(cat)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 8px', borderRadius: 6, cursor: creating ? 'wait' : 'pointer',
                      fontSize: '0.76rem', color: '#334155',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span style={{ color: '#16a34a', fontWeight: 700 }}>＋</span>
                    Create
                    <span style={{
                      backgroundColor: cat.color, color: contrastText(cat.color),
                      borderRadius: 999, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 600,
                    }}>
                      {search.trim()}
                    </span>
                    <span style={{ color: '#94a3b8' }}>in {cat.name}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(popover, document.body);
};

export default TagPicker;
